/**
 * 63.5's look after a hit (lane E; ARCHITECTURE §5.13 "63.5, the design", step 5 wires it — lane A): what a crater
 * looks like in each of the eight ground materials. Presentation only; nothing here is read by the sim.
 *
 * Two parts, for lane A's step 5 to call where the terrain colouring mixes today's one crater colour (core
 * planetColors.ts: `scorch` × the archetype's crater colour):
 *   - **the after-hit tint** (`afterHitTint`): the ground's colour at a point of the crater, by the material and how far
 *     out from the centre it is (t: 0 the centre … 1 the rim … up to 1.4 the throw beyond it) — scorch and pale chips on
 *     rock, churned dark soil, slumped and rippled sand, bared ground and thrown snow, a melt ring and a refrozen glaze
 *     on ice, splashed mud, grey ash, a salt crust broken to the darker ground under it;
 *   - **a decal or two** (`afterHitDecals`): small flat pieces laid on the crater — rock chips, soil clods, sand ripples,
 *     slush, radial glaze cracks, mud splats, ash speckle, salt plates.
 *
 * The material names mirror lane A's `GroundMaterial` (types/ground.ts on `feat/63.5-step1-ground-materials`); once it
 * lands, `GroundLook` becomes that type.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import { AFTER_HIT } from '../constants/groundLook';
import { afterHitRgb } from '../core/planetColors';
import type { GroundMaterial } from '../types/ground';
import type { PlanetState, TerrainEdit } from '../types';
import { getSurfaceRadius } from '../core/surface';

/** 63.5 step 5: the looks' names are the ground's materials (types/ground.ts). */
export type GroundLook = GroundMaterial;
export { AFTER_HIT, type DecalKind } from '../constants/groundLook';
export const GROUND_LOOKS: readonly GroundLook[] = ['soil', 'rock', 'sand', 'snow', 'ice', 'mud', 'ash', 'salt'];



/**
 * The ground's colour after a hit at a point `t` of the crater's radius out from its centre (0 the centre, 1 the rim;
 * beyond 1.4 untouched), starting from `base` (the terrain's colour there). Returns a new colour.
 */
export function afterHitTint(material: GroundLook, base: THREE.Color, t: number): THREE.Color {
  // 63.5 step 5: one implementation — the game's terrain colouring's (core/planetColors.ts afterHitRgb), in sRGB as the
  // terrain's colours are; the sheet converts its colour in and out
  if (t >= 1.4) return base.clone();   // past the throw: untouched (no round trip through sRGB)
  const c = base.getRGB({ r: 0, g: 0, b: 0 }, THREE.SRGBColorSpace);
  const o = afterHitRgb(material, c.r, c.g, c.b, t);
  return new THREE.Color().setRGB(o.r, o.g, o.b, THREE.SRGBColorSpace);
}

// ---------------------------------------------------------------------------
// Decals: small flat pieces laid on a crater of radius `r` (any units), centred at the origin, y up. Seeded.
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string, opacity = 1): THREE.MeshLambertMaterial {
  const key = `${color}:${opacity}`;
  let m = matCache.get(key);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true, transparent: opacity < 1, opacity, depthWrite: opacity >= 1 })); matCache.set(key, m); }
  return m;
}
function rng(seed: number): () => number { let s = (seed * 2654435761) >>> 0 || 1; return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; }; }

/** The decals of `material`'s hit on a crater of radius `r`, as one group (flat on y = 0; lay it on the crater's surface). */
export function afterHitDecals(material: GroundLook, r: number, seed = 1, person?: number): THREE.Group {
  const g = new THREE.Group(), R = rng(seed);
  // 69.71 (DECISIONS 296, the owner: "mounds on the map which can be passed through, almost like inverted craters"): a piece
  // is sized by the crater, but no bigger than a thing of its kind at a person's scale (`person`: a person's height in world
  // units, the host's) — a crash's crater at k 1/20 is 58 people across, and its clods were lumps 5–10 people wide on the
  // rim, drawn and never stood on. Positions still spread over the crater; without `person`, as before
  const cap = (size: number, people: number) => (person !== undefined ? Math.min(size, people * person) : size);
  const at = (minT: number, maxT: number) => { const a = R() * Math.PI * 2, t = minT + R() * (maxT - minT); return { x: Math.cos(a) * t * r, z: Math.sin(a) * t * r, a }; };
  const flat = (geo: THREE.BufferGeometry, color: string, x: number, z: number, yaw: number, y = cap(0.004 * r, 0.02), opacity = 1) => {
    const m = new THREE.Mesh(geo, mat(color, opacity)); m.position.set(x, y, z); m.rotation.y = yaw; g.add(m); return m;
  };
  for (const kind of AFTER_HIT[material].decals) {
    if (kind === 'chips') for (let i = 0; i < 14; i++) {   // pale shards of rock, thrown about the rim
      const p = at(0.6, 1.4), s = cap(r * (0.03 + R() * 0.05), 0.3);
      const m = flat(new THREE.TetrahedronGeometry(s, 0), i % 3 ? '#b8b2a6' : '#8e887c', p.x, p.z, R() * 6, s * 0.4);
      m.rotation.set(R() * 3, R() * 3, R() * 3);
    }
    if (kind === 'clods') for (let i = 0; i < 12; i++) {   // lumps of turned dark soil
      const p = at(0.7, 1.35), s = cap(r * (0.04 + R() * 0.05), 0.35);
      const m = flat(new THREE.IcosahedronGeometry(s, 0), i % 2 ? '#3a2c1e' : '#55432c', p.x, p.z, R() * 6, s * 0.35);
      m.scale.y = 0.6;
    }
    if (kind === 'ripples') for (let i = 0; i < 4; i++) {   // the slumped sand's ripples: arcs round the bowl
      const ring = new THREE.RingGeometry(r * (1.05 + i * 0.09), r * (1.08 + i * 0.09), 32, 1, R() * 2, Math.PI * (0.6 + R() * 0.6));
      ring.rotateX(-Math.PI / 2);
      flat(ring, i % 2 ? '#b09a70' : '#dccaa0', 0, 0, 0, 0.003 * r);
    }
    if (kind === 'slush') for (let i = 0; i < 8; i++) {   // grey slush in the bowl, thrown snow beyond
      const inBowl = i < 4, p = inBowl ? at(0.1, 0.6) : at(1.05, 1.35), s = cap(r * (inBowl ? 0.18 : 0.1) * (0.7 + R() * 0.5), 0.6);
      const disc = new THREE.CircleGeometry(s, 7); disc.rotateX(-Math.PI / 2);
      flat(disc, inBowl ? '#9aa2aa' : '#f4f8fc', p.x, p.z, R() * 6);
    }
    if (kind === 'glazeCracks') for (let i = 0; i < 7; i++) {   // radial cracks in the refrozen glaze
      const a = (i / 7) * Math.PI * 2 + R() * 0.4, len = cap(r * (0.45 + R() * 0.5), 3);
      const crack = new THREE.PlaneGeometry(cap(r * 0.025, 0.1), len); crack.rotateX(-Math.PI / 2);
      const m = flat(crack, '#2e4a5c', Math.cos(a) * (r * 0.55 + len / 2), Math.sin(a) * (r * 0.55 + len / 2), -a + Math.PI / 2);
      m.rotation.order = 'YXZ';
    }
    if (kind === 'splats') for (let i = 0; i < 9; i++) {   // mud splashed out past the rim, long blobs pointing away
      const p = at(1.0, 1.5), s = cap(r * (0.07 + R() * 0.08), 0.5);
      const blob = new THREE.CircleGeometry(s, 6); blob.rotateX(-Math.PI / 2); blob.scale(1.8, 1, 1);
      flat(blob, i % 2 ? '#2c2218' : '#3e3024', p.x, p.z, -p.a);
    }
    if (kind === 'ashSpeckle') for (let i = 0; i < 24; i++) {   // grey ash scattered round it
      const p = at(0.4, 1.6), s = cap(r * (0.015 + R() * 0.03), 0.15);
      const d = new THREE.CircleGeometry(s, 5); d.rotateX(-Math.PI / 2);
      flat(d, i % 3 ? '#c4c0ba' : '#5e5a56', p.x, p.z, R() * 6, cap(0.004 * r, 0.02), 0.85);
    }
    if (kind === 'saltPlates') for (let i = 0; i < 10; i++) {   // the crust broken into plates, tipped up round the rim
      const p = at(0.75, 1.2), w = cap(r * (0.1 + R() * 0.1), 0.6);
      const plate = new THREE.BoxGeometry(w, cap(r * 0.012, 0.05), w * (0.6 + R() * 0.5));
      const m = flat(plate, '#f4f2ea', p.x, p.z, -p.a, cap(r * 0.02, 0.08));
      m.rotation.order = 'YXZ'; m.rotation.x = 0.25 + R() * 0.35;   // tipped up, away from the centre
    }
  }
  return g;
}

/**
 * 63.5 step 5: a crater's decals laid on the ground as it is after the dig — the group at the crater's centre, up the
 * ground's normal there, each piece seated on the surface under it (the bowl curves; one flat group would float or sink).
 * Seeded by `seed` (the edit's place in the log). The caller adds it to the scene and disposes of it with the battle.
 */
export function layCraterDecals(planet: PlanetState, edit: Pick<TerrainEdit, 'point' | 'radius' | 'material'>, seed: number, person?: number): THREE.Group {
  const g = afterHitDecals(edit.material ?? 'soil', edit.radius, seed, person);
  const c = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z);
  const up = new THREE.Vector3(edit.point.x, edit.point.y, edit.point.z).sub(c).normalize();
  g.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
  g.position.copy(c).addScaledVector(up, getSurfaceRadius(planet, up));
  g.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(g.matrixWorld).invert(), w = new THREE.Vector3();
  for (const piece of g.children) {
    w.copy(piece.position).applyMatrix4(g.matrixWorld).sub(c).normalize();
    const d = { x: w.x, y: w.y, z: w.z };
    w.multiplyScalar(getSurfaceRadius(planet, d)).add(c).applyMatrix4(inv);
    piece.position.y = w.y;
  }
  return g;
}
